import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { updateChildProfileMutationRequest } from '../../../requests/update-child-profile.mutation.ts';
import { ProfileUpdateFormSchema } from '../../../types/accounts-validation.types.ts';

import { ACCOUNTS_SLICE_NAME, AccountsSliceState } from '../accounts.slice.types.ts';

interface Props {
  id: string;
  name: string;
  age: number;
}

const THUNK_NAME = `${ACCOUNTS_SLICE_NAME}/updateChildProfile`;

const handleError = (input: Props, message?: string): Error => {
  const errorMessage = message ?? getLocaleUtil().select('accounts:update_profile_failed');
  getToasterUtil().error(errorMessage);
  const error = new Error(errorMessage);
  error.name = ErrorTypeEnum.UPDATE_PROFILE_ERROR;

  getSentryUtil().trackError(
    error,
    {
      thunk: THUNK_NAME,
      errorType: ErrorTypeEnum.UPDATE_PROFILE_ERROR,
    },
    { input }
  );
  getTrackerUtil().track({
    event: TrackingEventEnum.CHILD_PROFILE_UPDATE_FAILED,
  });

  return error;
};

export const updateChildProfileThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(THUNK_NAME, async (props) => {
  const { id, name, age } = props;

  const parsedInput = ProfileUpdateFormSchema.safeParse(props);
  if (!parsedInput.success) {
    const error = handleError(props);
    throw error;
  }

  const result = await updateChildProfileMutationRequest(parsedInput.data);
  if (!result?.success) {
    const error = handleError(props, result.message);
    throw error;
  }

  getToasterUtil().success(getLocaleUtil().select('accounts:update_profile_success'));
  getTrackerUtil().track({
    event: TrackingEventEnum.CHILD_PROFILE_UPDATED,
    data: {
      id,
      name,
      age: String(age),
    },
  });
});

export const updateChildProfileThunkExtraReducers = (builder: ActionReducerMapBuilder<AccountsSliceState>): void => {
  builder.addCase(updateChildProfileThunk.pending, (state) => {
    state.isUpdateProfileLoading = true;
  });

  builder.addCase(updateChildProfileThunk.fulfilled, (state, action) => {
    state.isUpdateProfileLoading = false;

    state.availableProfiles = state.availableProfiles?.map((profile) => {
      if (profile.id === action.meta.arg.id) {
        return {
          ...profile,
          name: action.meta.arg.name,
          age: action.meta.arg.age,
        };
      }

      return profile;
    });
    if (state.currentProfile && state.currentProfile.id === action.meta.arg.id) {
      state.currentProfile.name = action.meta.arg.name;
      state.currentProfile.age = action.meta.arg.age;
    }
  });

  builder.addCase(updateChildProfileThunk.rejected, (state) => {
    state.isUpdateProfileLoading = false;
  });
};
